/**
 * Bot Monitoring Panel Component
 * Admin panel for monitoring bot detection activities
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import BotDetectionService, { BotAttempt } from '../../services/botDetectionService';
import IPBlockingService from '../../services/ipBlockingService';

const BotMonitoringPanel: React.FC = () => {
  const [botAttempts, setBotAttempts] = useState<BotAttempt[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAttempt, setSelectedAttempt] = useState<BotAttempt | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterRiskLevel, setFilterRiskLevel] = useState<string>('all');
  const [filterIsBot, setFilterIsBot] = useState<string>('all');

  const botDetectionService = BotDetectionService.getInstance();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [attempts, botStats] = await Promise.all([
        botDetectionService.getBotAttempts(100),
        botDetectionService.getBotStats()
      ]);
      
      setBotAttempts(attempts);
      setStats(botStats);
      setError(null);
    } catch (err) {
      console.error('Failed to load bot monitoring data:', err);
      setError('Failed to load bot monitoring data');
    } finally {
      setIsLoading(false);
    }
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getRiskLevelIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'critical': return <ErrorIcon />;
      case 'high': return <WarningIcon />;
      case 'medium': return <InfoIcon />;
      case 'low': return <CheckCircleIcon />;
      default: return <SecurityIcon />;
    }
  };

  const formatDate = (date: Date | string) => {
    if (typeof date === 'string') {
      return new Date(date).toLocaleString();
    }
    return date.toLocaleString();
  };

  const handleBlockIP = async (ip: string) => {
    try {
      await IPBlockingService.blockIP(
        ip,
        'Bot activity detected',
        'high',
        'admin'
      );
      await loadData();
    } catch (err) {
      console.error('Failed to block IP:', err);
    }
  };

  const filteredAttempts = botAttempts.filter(attempt => {
    if (filterRiskLevel !== 'all' && attempt.riskLevel !== filterRiskLevel) {
      return false;
    }
    if (filterIsBot !== 'all' && attempt.isBot.toString() !== filterIsBot) {
      return false;
    }
    return true;
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon color="primary" />
          <Typography variant="h5">Bot Detection Monitoring</Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadData}
          variant="outlined"
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Attempts
                </Typography>
                <Typography variant="h4">
                  {stats.totalAttempts}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Bot Attempts
                </Typography>
                <Typography variant="h4" color="error">
                  {stats.botAttempts}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Human Attempts
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.humanAttempts}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Avg Confidence
                </Typography>
                <Typography variant="h4">
                  {stats.averageConfidence}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Risk Level</InputLabel>
          <Select
            value={filterRiskLevel}
            onChange={(e) => setFilterRiskLevel(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={filterIsBot}
            onChange={(e) => setFilterIsBot(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Bot</MenuItem>
            <MenuItem value="false">Human</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Bot Attempts Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>User Agent</TableCell>
                <TableCell>Risk Level</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Is Bot</TableCell>
                <TableCell>reCAPTCHA Score</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAttempts.map((attempt, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(attempt.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {attempt.ipAddress || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={attempt.userAgent}>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {attempt.userAgent.substring(0, 50)}...
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getRiskLevelIcon(attempt.riskLevel)}
                      label={attempt.riskLevel}
                      color={getRiskLevelColor(attempt.riskLevel) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {attempt.confidence}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={attempt.confidence}
                        sx={{ width: 50, height: 4 }}
                        color={attempt.confidence > 70 ? 'error' : attempt.confidence > 40 ? 'warning' : 'success'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={attempt.isBot ? 'Bot' : 'Human'}
                      color={attempt.isBot ? 'error' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {attempt.recaptchaScore !== undefined ? (
                      <Typography variant="body2">
                        {(attempt.recaptchaScore * 100).toFixed(1)}%
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          onClick={() => {
                            setSelectedAttempt(attempt);
                            setDetailsOpen(true);
                          }}
                          size="small"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {attempt.isBot && attempt.ipAddress && (
                        <Tooltip title="Block IP">
                          <IconButton
                            onClick={() => handleBlockIP(attempt.ipAddress!)}
                            size="small"
                            color="error"
                          >
                            <BlockIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            Bot Detection Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAttempt && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Timestamp
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedAttempt.timestamp)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    IP Address
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {selectedAttempt.ipAddress || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Risk Level
                  </Typography>
                  <Chip
                    icon={getRiskLevelIcon(selectedAttempt.riskLevel)}
                    label={selectedAttempt.riskLevel}
                    color={getRiskLevelColor(selectedAttempt.riskLevel) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Confidence
                  </Typography>
                  <Typography variant="body2">
                    {selectedAttempt.confidence}%
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                User Agent
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                {selectedAttempt.userAgent}
              </Typography>

              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                Detection Reasons
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {selectedAttempt.reasons.map((reason, index) => (
                  <Chip key={index} label={reason} size="small" color="warning" />
                ))}
              </Box>

              {selectedAttempt.recaptchaScore !== undefined && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                    reCAPTCHA Score
                  </Typography>
                  <Typography variant="body2">
                    {(selectedAttempt.recaptchaScore * 100).toFixed(1)}%
                  </Typography>
                </Box>
              )}

              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, mt: 2 }}>
                Behavior Data
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Typing Speed: {selectedAttempt.behaviorData.typingSpeed.toFixed(2)} chars/sec
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Mouse Movements: {selectedAttempt.behaviorData.mouseMovements}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Form Fill Time: {(selectedAttempt.behaviorData.formFillTime / 1000).toFixed(1)}s
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Copy-Paste: {selectedAttempt.behaviorData.copyPasteDetected ? 'Yes' : 'No'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Tab Switches: {selectedAttempt.behaviorData.tabSwitchCount}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Error Rate: {selectedAttempt.behaviorData.errorRate}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BotMonitoringPanel;
